import { AddOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import { Filter, PoolSpinner, SearchFilter } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { InvoiceNumberSequence, Locale } from '@synergy/models';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { PoolDocumentTitle } from '@synergy/components-next';
import { useEffect, useState } from 'react';

import { ConfigurationTab } from '../models/configuration-tab';
import { InvoiceNumberSequenceModal } from '../invoice-number-sequence/invoice-number-sequence-modal';
import { InvoiceNumberSequenceOverview } from '../invoice-number-sequence/invoice-number-sequence-overview';
import { PaloActionMenu } from '../../../../common/components/palo/palo-action-menu';
import { PaloConfigurationShell } from '../../palo/palo-configuration-shell';
import { PaloLanguageSwitch } from '../../../../common/components/palo/palo-language-switch';
import { PaymentTerm } from '../../../../common/models/payment-term';
import { PaymentTermDetailModal } from '../payment-terms/payment-terms-detail-modal';
import { PaymentTermsOverview } from '../payment-terms/payment-terms-overview';
import { TaxRate } from '../../../../common/models/tax-rate';
import { TaxRateDetailModal } from '../tax-rates/tax-rates-detail-modal';
import { TaxRatesOverview } from '../tax-rates/tax-rates-overview';
import { changeSelectedLanguage, resetSelectedLanguage } from '../../../../common/actions/selected-language-actions';
import { defaultSearchParamsSelector } from '../../../../store/default-searchparams-selector';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export const PaloConfigurationFinanceOverview = () => {
    const dispatch = useThunkDispatch();
    const location = useLocation();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const supportedLocales = useAppSelector(state => state.supportedLocales);

    const { requesting: paymentTermsRequesting } = useAppSelector(state => state.paymentTerms);
    const { requesting: taxRatesRequesting } = useAppSelector(state => state.taxRates);
    const defaultSearchParams = useAppSelector(defaultSearchParamsSelector);
    const [searchParams, setSearchParams] = useSearchParams(defaultSearchParams);

    const [isTaxRateModalOpen, setIsTaxRateModalOpen] = useState(false);
    const [isPaymentTermModalOpen, setIsPaymentTermModalOpen] = useState(false);
    const [selectedTaxRate, setSelectedTaxRate] = useState<TaxRate | undefined>(undefined);
    const [selectedPaymentTerm, setSelectedPaymentTerm] = useState<PaymentTerm | undefined>(undefined);
    const [selectedInvoiceNumberSequence, setSelectedInvoiceNumberSequence] = useState<
        InvoiceNumberSequence | undefined
    >(undefined);
    const [isInvoiceNumberSequenceModalOpen, setIsInvoiceNumberSequenceModalOpen] = useState(false);

    useEffect(() => {
        dispatch(resetSelectedLanguage());
    }, [dispatch]);

    const handleInvoiceNumberSequenceEdit = (invoiceNumberSequence: InvoiceNumberSequence) => {
        setSelectedInvoiceNumberSequence(invoiceNumberSequence);
        setIsInvoiceNumberSequenceModalOpen(true);
    };
    const handleTaxRatesEdit = (taxRate: TaxRate) => {
        setSelectedTaxRate(taxRate);
        setIsTaxRateModalOpen(true);
    };

    const handlePaymentTermEdit = (paymentTerm: PaymentTerm) => {
        setSelectedPaymentTerm(paymentTerm);
        setIsPaymentTermModalOpen(true);
    };

    const handleNewInvoiceNumberSequence = () => {
        setSelectedInvoiceNumberSequence(undefined);
        setIsInvoiceNumberSequenceModalOpen(true);
    };
    const handleNewTaxRate = () => {
        setSelectedTaxRate(undefined);
        setIsTaxRateModalOpen(true);
    };

    const handleNewPaymentTerm = () => {
        setSelectedPaymentTerm(undefined);
        setIsPaymentTermModalOpen(true);
    };

    const handleTaxRateModalClose = () => {
        setSelectedTaxRate(undefined);
        setIsTaxRateModalOpen(false);
    };

    const handlePaymentTermModalClose = () => {
        setSelectedPaymentTerm(undefined);
        setIsPaymentTermModalOpen(false);
    };

    const handleInvoiceNumberSequenceModalClose = () => {
        setSelectedInvoiceNumberSequence(undefined);
        setIsInvoiceNumberSequenceModalOpen(false);
    };
    const handleLanguageSwitch = (locale: Locale) => {
        dispatch(changeSelectedLanguage(locale));
    };

    return (
        <PoolSpinner enabled={paymentTermsRequesting || taxRatesRequesting} sx={{ display: 'block', height: '100%' }}>
            <Filter searchParams={searchParams} onUpdateSearchParams={setSearchParams}>
                <PaloConfigurationShell
                    title="app.configuration.title.finance"
                    tabs={[
                        { value: ConfigurationTab.PaymentTerms, label: 'app.configuration.paymentTerms' },
                        { value: ConfigurationTab.TaxRates, label: 'app.configuration.taxRates' },
                        {
                            value: ConfigurationTab.InvoiceNumberSequence,
                            label: 'app.configuration.invoiceNumberSequence',
                        },
                    ]}
                    actions={
                        <>
                            <Box sx={{ width: 220 }}>
                                <SearchFilter />
                            </Box>
                            {!location.pathname.includes(ConfigurationTab.InvoiceNumberSequence) && (
                                <PaloLanguageSwitch />
                            )}
                            <PaloActionMenu
                                primary={true}
                                label={<FormattedMessage id="app.dashboard.new" />}
                                icon={<AddOutlined />}
                                items={[
                                    {
                                        label: <FormattedMessage id="app.configuration.newPaymentTerm" />,
                                        onClick: handleNewPaymentTerm,
                                    },
                                    {
                                        label: <FormattedMessage id="app.configuration.newTaxRate" />,
                                        onClick: handleNewTaxRate,
                                    },
                                    {
                                        label: <FormattedMessage id="app.configuration.newInvoiceNumberSequence" />,
                                        onClick: handleNewInvoiceNumberSequence,
                                    },
                                ]}
                            />
                        </>
                    }
                >
                    <Routes>
                        <Route
                            path={ConfigurationTab.PaymentTerms}
                            element={<PaymentTermsOverview onEdit={handlePaymentTermEdit} />}
                        />
                        <Route
                            path={ConfigurationTab.TaxRates}
                            element={<TaxRatesOverview onEdit={handleTaxRatesEdit} />}
                        />
                        <Route
                            path={ConfigurationTab.InvoiceNumberSequence}
                            element={<InvoiceNumberSequenceOverview onEdit={handleInvoiceNumberSequenceEdit} />}
                        />
                        <Route
                            path="*"
                            element={
                                <Navigate
                                    to={{ pathname: ConfigurationTab.PaymentTerms, search: searchParams.toString() }}
                                    replace={true}
                                />
                            }
                        />
                    </Routes>
                </PaloConfigurationShell>
            </Filter>

            <TaxRateDetailModal
                visible={isTaxRateModalOpen}
                onClose={handleTaxRateModalClose}
                taxRate={selectedTaxRate}
            />
            <PaymentTermDetailModal
                visible={isPaymentTermModalOpen}
                onClose={handlePaymentTermModalClose}
                paymentTerm={selectedPaymentTerm}
            />
            <InvoiceNumberSequenceModal
                visible={isInvoiceNumberSequenceModalOpen}
                onClose={handleInvoiceNumberSequenceModalClose}
                invoiceNumberSequence={selectedInvoiceNumberSequence}
            />
        </PoolSpinner>
    );
};
